import { useAssemblyCadModelsBasePath } from '@/components/Analysis/modules/assembly/useAssemblyCadModelsBasePath';
import * as poiState from '@/components/Analysis/modules/pointOfInterest/state';
import * as state from '@/components/Analysis/state';
import { BrightnessControls } from '@/components/shared/BrightnessControls';
import { ContrastControls } from '@/components/shared/ContrastControls';
import { EdlStrengthSlider } from '@/components/shared/EdlStrengthSlider';
import { PointSizeControls } from '@/components/shared/PointSizeControls';
import { SideDrawer } from '@/components/shared/SideDrawer';
import { useFeatureFlag, useHavePermission } from '@/hooks';
import { useDrawerWidths } from '@/hooks/useDrawerWidths';
import { DrawersProps, Permissions } from '@/types';
import { ImageType } from '@fabric-web/3d-viewer';
import ColorLens from '@mui/icons-material/ColorLens';
import { Stack } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback } from 'react';
import { DefectColorLegend } from '../DefectColorLegend';
import { MarkerLabelControls } from './MakrerLabelControls';
import { RgbPointColorToggle } from './RgbPointColorToggle';
import { ToggleItemVisibility } from './ToggleItemVisibility';
import { StyledCollapse } from './styles';
import { IsolateVoiPointCloud } from './IsolateVoiPointCloud';

export const Visibility = ({ allClosed, isOpen, handleOpen }: DrawersProps) => {
  const selectedPoiId = useAtomValue(poiState.selectedPointOfInterestId);
  const [areSphericalsVisible, setAreSphericalsVisible] = useAtom(state.areSphericalsVisible);
  const [neighbouringImagesMode, setNeighbouringImagesMode] = useAtom(state.neighbouringImagesMode);

  const [showPointCloudInSpherical, setShowPointCloudInSpherical] = useAtom(
    state.showPointCloudInSpherical
  );
  const [showPointsOutsideViewpoint, setShowPointsOutsideViewpoint] = useAtom(
    state.showPointsOutsideViewpoint
  );
  const [showAssemblyCadModel, setShowAssemblyCadModel] = useAtom(state.showAssemblyCadModel);
  const selectedAssemblyDetails = useAtomValue(state.selectedAssemblyDetails);
  const { hasAssemblyCadModels } = useAssemblyCadModelsBasePath();

  const inspectionMetadata = useAtomValue(state.inspectionMetadata);
  const hasPhotogrsammetryTiles = !!inspectionMetadata?.photogrammetryTilesPath;

  const [showPhotogrammetryTiles, setShowPhotogrammetryTiles] = useAtom(
    state.showPhotogrammetryTiles
  );

  const [showPointCloud, setShowPointCloud] = useAtom(state.showPointCloud);

  const [areDecksVisible, setAreDecksVisible] = useAtom(state.areDecksVisible);
  const [arePoisVisible, setArePoisVisible] = useAtom(state.arePoisVisible);
  const [showOnlySelectedPoi, setShowOnlySelectedPoi] = useAtom(state.showOnlySelectedPoi);

  const currentSpherical = useAtomValue(state.selectedSpherical);
  const hasRgb = useAtomValue(state.hasRgb);
  const { leftDrawerWidth } = useDrawerWidths();

  const canReadDefects = useHavePermission(Permissions.READ_DEFECTS);

  const isBetaUser = useFeatureFlag(['admin', 'beta-user']);
  const isAlphaUser = useFeatureFlag(['admin', 'alpha-user']);
  const canAdjustBrightnessAndContrast = useFeatureFlag(['admin', 'ait-engineer', 'beta-user']);

  const toggleSphericalVisibility = useCallback(() => {
    setAreSphericalsVisible(!areSphericalsVisible);
  }, [areSphericalsVisible, setAreSphericalsVisible]);

  const toggleNeighbouringImagesEnabled = useCallback(() => {
    setNeighbouringImagesMode((current) => ({ ...current, isEnabled: !current.isEnabled }));
  }, [setNeighbouringImagesMode]);

  const toggleNeighbouringImagesShowImages = useCallback(() => {
    setNeighbouringImagesMode((current) => ({ ...current, showImages: !current.showImages }));
  }, [setNeighbouringImagesMode]);

  const toggleNeighbouringImagesShowFrames = useCallback(() => {
    setNeighbouringImagesMode((current) => ({ ...current, showFrames: !current.showFrames }));
  }, [setNeighbouringImagesMode]);

  const togglePointCloudVisibility = () => {
    setShowPointCloudInSpherical((current) => !current);
  };

  const togglePointsOutsideViewpoint = () => {
    setShowPointsOutsideViewpoint((current) => !current);
  };

  const toggleDecksVisibility = useCallback(() => {
    setAreDecksVisible((currentAreDecksVisible) => {
      return !currentAreDecksVisible;
    });
  }, [setAreDecksVisible]);

  const toggleAssemblyCadModel = useCallback(() => {
    setShowAssemblyCadModel((current) => !current);
  }, [setShowAssemblyCadModel]);

  const togglePhotogrammetryTiles = useCallback(() => {
    setShowPhotogrammetryTiles((current) => !current);
  }, [setShowPhotogrammetryTiles]);

  const togglePointCloud = useCallback(() => {
    setShowPointCloud((current) => !current);
  }, [setShowPointCloud]);

  const togglePoiVisibility = useCallback(() => {
    setArePoisVisible((current) => !current);
    setShowOnlySelectedPoi(false);
  }, [setArePoisVisible, setShowOnlySelectedPoi]);

  const toggleShowOnlySelectedPoi = useCallback(() => {
    setShowOnlySelectedPoi((current) => !current);
  }, [setShowOnlySelectedPoi]);

  return (
    <div data-testid="visibility">
      <SideDrawer
        allDrawersClosed={allClosed}
        isOpen={isOpen}
        handleOpen={handleOpen}
        icon={<ColorLens />}
        position="left"
        width={leftDrawerWidth}
        label="Visibility settings"
        iconStyles={{ top: 10, zIndex: '102 !important' }}
        scrollable
        labelStyles={{ marginLeft: 0 }}
      >
        <Stack direction="column" spacing={2}>
          {canReadDefects && (
            <StyledCollapse in>
              <DefectColorLegend />
            </StyledCollapse>
          )}

          <PointSizeControls />

          <EdlStrengthSlider title="Enhance Points" />
          {canAdjustBrightnessAndContrast && (
            <>
              <BrightnessControls disabled={!currentSpherical} />
              <ContrastControls disabled={!currentSpherical} />
            </>
          )}
          {currentSpherical && (
            <>
              <ToggleItemVisibility
                label="Point Cloud"
                isVisible={showPointCloudInSpherical}
                toggleVisibility={togglePointCloudVisibility}
                tooltipTitle='"Press Alt + A"'
              />
              <ToggleItemVisibility
                label="Points outside viewpoint"
                isVisible={showPointsOutsideViewpoint}
                toggleVisibility={togglePointsOutsideViewpoint}
                disabled={!showPointCloudInSpherical}
              />
            </>
          )}
          <ToggleItemVisibility
            label="Viewpoints"
            isVisible={areSphericalsVisible}
            toggleVisibility={toggleSphericalVisibility}
            tooltipTitle='"Press Alt + V"'
          />
          {isBetaUser && currentSpherical?.imageType === ImageType.Rectilinear && (
            <>
              <ToggleItemVisibility
                label="Neighbouring images"
                isVisible={neighbouringImagesMode.isEnabled}
                toggleVisibility={toggleNeighbouringImagesEnabled}
              />
              {neighbouringImagesMode.isEnabled && (
                <Stack sx={{ paddingLeft: '20px' }}>
                  <ToggleItemVisibility
                    label="Show images"
                    isVisible={neighbouringImagesMode.showImages}
                    toggleVisibility={toggleNeighbouringImagesShowImages}
                  />
                  <ToggleItemVisibility
                    label="Show frames"
                    isVisible={neighbouringImagesMode.showFrames}
                    toggleVisibility={toggleNeighbouringImagesShowFrames}
                  />
                </Stack>
              )}
            </>
          )}
          {!currentSpherical && (
            <ToggleItemVisibility
              label="Deck Grids"
              isVisible={areDecksVisible}
              toggleVisibility={toggleDecksVisibility}
            />
          )}
          {isAlphaUser && (
            <ToggleItemVisibility
              label="Component CAD model"
              isVisible={showAssemblyCadModel}
              toggleVisibility={toggleAssemblyCadModel}
              disabled={!hasAssemblyCadModels || !selectedAssemblyDetails?.cadModelPath}
              disabledReason="CAD not available"
            />
          )}
          {isBetaUser && hasPhotogrsammetryTiles && (
            <>
              <ToggleItemVisibility
                label="Point cloud"
                isVisible={showPointCloud}
                toggleVisibility={togglePointCloud}
              />
              <ToggleItemVisibility
                label="Photogrammetry 3d-Tiles"
                isVisible={showPhotogrammetryTiles}
                toggleVisibility={togglePhotogrammetryTiles}
              />
            </>
          )}
          <ToggleItemVisibility
            label="Show all POI’s"
            isVisible={arePoisVisible}
            toggleVisibility={togglePoiVisibility}
            tooltipTitle='"Press Alt + P"'
          />
          <ToggleItemVisibility
            label="Show only selected POI"
            isVisible={showOnlySelectedPoi}
            toggleVisibility={toggleShowOnlySelectedPoi}
            disabledReason={
              !arePoisVisible
                ? "Only available when Show all POI's is enabled"
                : 'Select a POI first'
            }
            disabled={!arePoisVisible || !selectedPoiId}
          />
          <MarkerLabelControls />
          {hasRgb && <RgbPointColorToggle />}
          {isAlphaUser && <IsolateVoiPointCloud />}
        </Stack>
      </SideDrawer>
    </div>
  );
};
